import { createColumnHelper } from "@tanstack/react-table";
import { MdMail, MdSms } from "react-icons/md";
import ExpandableDataTable, { DetailGrid, DetailItem } from "components/admin/ExpandableDataTable";
import StatusBadge from "components/admin/StatusBadge";
import usePaginatedResource from "hooks/usePaginatedResource";

const columnHelper = createColumnHelper();
const STATUS_TONE = { SENT: "green", FAILED: "red" };
const CATEGORY_LABEL = {
  WELCOME: "Welcome",
  PASSWORD_RESET: "Password reset",
  DEPOSIT_CONFIRMED: "Deposit confirmed",
  DEPOSIT_FAILED: "Deposit failed",
  WITHDRAWAL_PAID: "Withdrawal paid",
  WITHDRAWAL_FAILED: "Withdrawal failed",
};
const PAGE_SIZE = 20;

const exportColumns = [
  { header: "Sent", accessorKey: "createdAt" },
  { header: "Channel", accessorKey: "channel" },
  { header: "Event", accessorKey: "category" },
  { header: "Sent to", accessorKey: "recipient" },
  { header: "Subject", accessorKey: "subject" },
  { header: "Message", accessorKey: "preview" },
  { header: "Status", accessorKey: "status" },
  { header: "Error", accessorKey: "error" },
];

function ChannelIcon({ channel }) {
  return channel === "email" ? <MdMail className="text-gray-400" aria-label="Email" /> : <MdSms className="text-gray-400" aria-label="SMS" />;
}

export default function NotificationsPage() {
  const { items, total, loading, error, page, setPage, refetch, exportAll } = usePaginatedResource("/portal/notifications", {
    pageSize: PAGE_SIZE,
    itemsKey: "notifications",
  });

  const columns = [
    columnHelper.accessor("category", {
      header: "Event",
      cell: (info) => (
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-navy-700 dark:text-white">
          <ChannelIcon channel={info.row.original.channel} />
          {CATEGORY_LABEL[info.getValue()] || info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("recipient", {
      header: "Sent to",
      cell: (info) => <p className="max-w-[220px] truncate text-sm text-navy-700 dark:text-white">{info.getValue()}</p>,
      meta: { hideBelow: "md" },
    }),
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => <StatusBadge label={info.getValue()} tone={STATUS_TONE[info.getValue()] || "gray"} />,
    }),
    columnHelper.accessor("createdAt", {
      header: "Sent",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{new Date(info.getValue()).toLocaleString()}</p>,
      meta: { hideBelow: "sm" },
    }),
  ];

  return (
    <ExpandableDataTable
      title="Notifications sent to your players"
      subtitle="Every email and SMS the platform has sent to a player of yours. Expand one to read what was sent."
      columns={columns}
      exportColumns={exportColumns}
      data={items}
      loading={loading}
      error={error}
      onRetry={refetch}
      emptyMessage="No notifications have gone out to your players yet."
      page={page}
      pageSize={PAGE_SIZE}
      total={total}
      onPageChange={setPage}
      exportFilename="notifications"
      onExportAll={exportAll}
      rowLabel={(n) => `${CATEGORY_LABEL[n.category] || n.category} to ${n.recipient}`}
      renderDetail={(n) => (
        <DetailGrid>
          <DetailItem label="Channel">{n.channel === "email" ? "Email" : "SMS"}</DetailItem>
          <DetailItem label="Sent to">{n.recipient}</DetailItem>
          <DetailItem label="Sent at">{new Date(n.createdAt).toLocaleString()}</DetailItem>
          {n.subject && (
            <DetailItem label="Subject" span={3}>
              {n.subject}
            </DetailItem>
          )}
          <DetailItem label="Message" span={3}>
            <span className="whitespace-pre-wrap">{n.preview}</span>
          </DetailItem>
          <DetailItem label="Delivery">
            <StatusBadge label={n.status} tone={STATUS_TONE[n.status] || "gray"} />
          </DetailItem>
          {n.error && (
            <DetailItem label="Error" span={2}>
              <span className="text-red-500">{n.error}</span>
            </DetailItem>
          )}
          <DetailItem label="Notification ID" mono>
            {n.id}
          </DetailItem>
        </DetailGrid>
      )}
    />
  );
}
